import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Table,
    Paper,
    TableBody,
    IconButton,
    Switch,
    Stack,
} from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import LinkNavegacao from '../../../common/Link';
import PageTitle from '../../../common/PageTitle';
import { DepartamentoIcon, EditIcon, TrashIcon } from '../../../common/Icons';
import { atualizaDepartamento, deletaDepartamento, listaDepartamentos } from './configs/functions';
import { DepartmentForm } from './DepartmentForm';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { useAppContext } from '../../../../contexts/AppContext';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { listaEmpresas } from '../companies/configs/functions';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { useModuleContext } from '../../../../contexts/ModuleProvider';
import { CustomToolbarExportTable } from '../../../common/CustomToolbarExportTable';
import {FiltersButton} from "../../../common/FiltersButton";
import {useQuery} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import {ItemsTableFilter} from "./components/ItemsTableFilter";

const DepartmentRow = ({ data, actionsAllowed, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData }) => {
    const [active, setActive] = useState(data.status);
    const [openDialog, setOpenDialog] = useState(false);

    async function handleToggleStatus() {
        if (!hasPermission(["admin", "admin_departamentos", "update_departamentos"])) {
            handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        try {
            const res = await atualizaDepartamento(data.id, { status: !active })
            if (res) {
                const message = active ? 'Departamento inativado com sucesso' : 'Departamento ativado com sucesso'
                handleExibirAlerta('Sucesso', message, 'success')
                setActive(!active)
                handleUpdateStatus(data.id, !data.status)
            }
        } catch (error) {
            handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
            console.log(error)
        }
    }

    const deleteDepartment = () => {
        const id = data.id;
        deletaDepartamento(id)
            .then((response) => {
                const message = response.data.message ?? 'Departamento excluído com sucesso'
                handleExibirAlerta('Sucesso', message, 'success');
                handleUpdateStatus(id, false);
                setActive(false);
            }).catch((error) => {
                const message = error.response.data.message ?? 'Erro ao executar operação'
                handleExibirAlerta('Erro', message, 'error');
            }).finally(() => {
                if (updateData) updateData();
        })

        setOpenDialog(false)
    }

    return (
        <>
            <TableRow key={data.id}>
                <TableCell>{data.titulo}</TableCell>
                <TableCell>{data.codExterno}</TableCell>
                <TableCell>{data.coordenador.nome}</TableCell>
                <TableCell>{data.descricao}</TableCell>

                <TableCell>
                    <Switch
                        checked={active}
                        onChange={handleToggleStatus}
                        disabled={!hasPermission(["admin", "admin_departamentos", "update_departamentos"]) || !actionsAllowed}
                    />
                </TableCell>

                <TableCell align='right' sx={{ minWidth: 110 }}>
                    {hasPermission(["admin", "admin_departamentos", "update_departamentos"]) &&
                        <IconButton
                            aria-label="Editar"
                            onClick={() => handleOpenEditForm(data)}
                        >
                            {actionsAllowed ? <EditIcon /> : <Visibility color='primary' />}
                        </IconButton>
                    }

                    {hasPermission(["admin", "admin_departamentos", "delete_departamentos"]) && actionsAllowed &&
                        <IconButton
                            aria-label="Excluir"
                            onClick={() => setOpenDialog(true)}
                        >
                            <TrashIcon />
                        </IconButton>
                    }
                </TableCell>
            </TableRow>

            <ConfirmDialog
                description={'Tem certeza que deseja excluir este Departamento?'}
                title={`Excluir Departamento ${data.nome}`}
                goAction={deleteDepartment}
                handleClose={setOpenDialog}
                state={openDialog}
            />
        </>
    )
}

function Departments() {
    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();
    const { verifyIntegrationMode, getEmpresasFilteredByModule } = useModuleContext();

    const [moduleActionsAllowed, setModuleActionsAllowed] = useState(null);

    // estados para controle de paginacao
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);

    const [departamentos, setDepartamentos] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const columns = useMemo(() => [
        { field: 'titulo', headerName: 'Departamento' },
        { field: 'codExterno', headerName: 'Cód. Externo' },
        { field: 'descricao', headerName: 'Descrição' },
        { field: 'empresa.nomeFantasia', headerName: 'Empresa', valueGetter: (params) => params.row.empresa.nomeFantasia },
        { field: 'coordenador.nome', headerName: 'Coordenador', valueGetter: (params) => params.row.coordenador.nome },
        { field: 'setor[0].titulo', headerName: 'Setor', valueGetter: (params) => params.row.setor[0]?.titulo },
        { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status === 1 ? 'Ativo' : 'Inativo' },
    ], []);

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;

            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

            setEmpresaIdSession(selectedEmpresaId);

            setSearchParams((state) => {
                state.set('id_empresa', String(selectedEmpresaId));
                state.set('page', String(page + 1));
                state.set('perPage', String(rowsPerPage));

                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const queryFilters = {
        id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
        titulo: searchParams.get('nome'),
        cod_externo: searchParams.get('cod_externo'),
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKeyListDepartments = ['listOfDepartments', queryFilters];
    const { data: departamentosData, isLoading, error: departamentosError, refetch } = useQuery({
        queryKey: queryKeyListDepartments,
        queryFn: () => listaDepartamentos(queryFilters),
        enabled: !!searchParamPage && !!searchParamPerPage,
    });

    const updateDepartmentStatus = (categoryId, status) => setDepartamentos(
        departamentos.map((item) => {
            if (item.id === categoryId) {
                item.status = status;
            }
            return item;
        })
    );

    function handleEditDepartment(item) {
        setSelectedDepartment(item);
        setOpenForm(true);
    }

    useEffect(() => {
        if (listaEmpresasError) {
            exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
        }

        if (departamentosError) {
            exibirAlerta('Erro', 'Erro ao carregar departamentos', 'error');
        }
    }, [exibirAlerta, listaEmpresasError, departamentosError])

    useEffect(() => {
        if (departamentosData) {
            setDepartamentos(departamentosData.data.data);
            setNumTotalItems(departamentosData.data.numero_total);
            setSelectedEmpresaId(searchParams.get('id_empresa'));
            setEmpresas(empresasData.data.data)
        }
    }, [departamentosData]);

    useEffect(() => {
        if (selectedEmpresaId) {
            const empresaHasActionsAllowed = verifyIntegrationMode('Departamento', selectedEmpresaId);
            setModuleActionsAllowed(empresaHasActionsAllowed);
        }
    }, [selectedEmpresaId, verifyIntegrationMode]);

    useEffect(() => {
        if (openForm) return;
        setSelectedDepartment(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

    }, [openForm, searchParams]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_departamentos", "list_departamentos"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    return (<>
        <Helmet title="Departamentos" defer={false} />

        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={3}
        >
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to="/">
                        <HomeIcon fontSize="small" />
                    </LinkNavegacao>
                    <Typography variant='span'>Gerenciamento</Typography>
                    <LinkNavegacao to="/gerenciamento/organizacao">Organização</LinkNavegacao>
                    <Typography variant='span'>Departamentos</Typography>
                </Breadcrumbs>

                <PageTitle
                    icon={<DepartamentoIcon fontSize='large' />}
                    title="Departamentos"
                    description='Administre os departamentos da sua(s) empresa(s).'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    {hasPermission(["admin", "admin_departamentos", "create_departamentos"]) &&
                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setOpenForm(true)}
                            >
                                Cadastrar departamento
                            </Button>
                        </Typography>
                    }
                </Stack>
            </Grid>

            <ItemsTableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoading}
                empresas={empresasData?.data?.data || []}
                item={"Departamento"}
            />

            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <CustomToolbarExportTable
                        rows={departamentos}
                        columns={columns}
                        reportName="relatorio_departamentos"
                        reportPdfTitle="RELATÓRIO DE DEPARTAMENTOS"
                        getDataWithoutPaginate={() => listaDepartamentos({ id_empresas: [selectedEmpresaId] })}
                        getSelectedEmpresa={() => empresas.find(empresa => empresa.id === Number(selectedEmpresaId))}
                    />

                    <Table aria-label="Departamentos">
                        <TableHead>
                            <TableRow>
                                <TableCell>Departamento</TableCell>
                                <TableCell>Cód. Externo</TableCell>
                                <TableCell>Coordenador(a)</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? <LoadingTableRow />
                                : departamentos && departamentos?.length > 0
                                    ? departamentos.map((item) =>
                                        <DepartmentRow
                                            key={item.id}
                                            data={item}
                                            handleExibirAlerta={exibirAlerta}
                                            handleOpenEditForm={handleEditDepartment}
                                            handleUpdateStatus={updateDepartmentStatus}
                                            updateData={refetch}
                                            actionsAllowed={moduleActionsAllowed}
                                        />
                                    )
                                    : <EmptyTableRow />
                            }
                        </TableBody>
                    </Table>

                    <CustomTablePagination
                        numTotalItems={numTotalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </TableContainer>
            </Grid>
        </Grid>

        <DepartmentForm
            open={openForm}
            setOpen={setOpenForm}
            selectedDepartment={selectedDepartment}
            empresas={getEmpresasFilteredByModule('Departamento', empresas, !selectedDepartment)}
            actionsAllowed={moduleActionsAllowed}
            updateData={refetch}
        />
    </>);
}

export default Departments;